/**
 * CloseAccountModal Component
 * Confirmation modal for closing a manual account
 */
import { useState, useEffect } from 'react';
import Button from '@/components/ui/Button';
import Alert from '@/components/ui/Alert';
import { formatCurrency } from '@/utils/format';
import { useCloseAccount } from '@/hooks/useAccounts';
import { ApiClientError } from '@/services/api';
import { toast } from 'sonner';
import type { AccountWithConnection } from '@/services/accounts';

export interface CloseAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  account: AccountWithConnection | null;
}

export default function CloseAccountModal({
  isOpen,
  onClose,
  account,
}: CloseAccountModalProps) {
  const [error, setError] = useState<string | null>(null);
  const closeMutation = useCloseAccount();

  // Reset error when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setError(null);
    }
  }, [isOpen]);

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleConfirm = () => {
    if (!account) return;

    setError(null);

    // Get today's date in YYYY-MM-DD format
    const today = new Date().toISOString().split('T')[0];

    closeMutation.mutate(
      {
        id: account.id,
        data: {
          closingDate: today,
          closingBalance: account.balance,
        },
      },
      {
        onSuccess: () => {
          toast.success(`Account "${account.name}" has been closed`);
          handleClose();
        },
        onError: (err) => {
          if (err instanceof ApiClientError) {
            setError(err.detail);
          } else {
            setError('Failed to close account. Please try again.');
          }
        },
      }
    );
  };

  if (!isOpen || !account) return null;

  // Calculate display balance (negative for liabilities)
  const displayBalance =
    account.type === 'credit' || account.type === 'loan'
      ? -Math.abs(account.balance)
      : account.balance;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="close-account-modal-title"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
        {/* Warning icon */}
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-yellow-100 mb-4">
          <WarningIcon className="h-6 w-6 text-yellow-600" />
        </div>

        <h2
          id="close-account-modal-title"
          className="text-lg font-semibold text-gray-900 text-center mb-2"
        >
          Close Account
        </h2>

        <p className="text-sm text-gray-600 text-center mb-4">
          Are you sure you want to close{' '}
          <span className="font-medium text-gray-900">{account.name}</span>?
        </p>

        {/* Account details */}
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <div className="flex justify-between items-center text-sm">
            <span className="text-gray-600">Current Balance</span>
            <span className="font-medium text-gray-900">
              {formatCurrency(displayBalance, account.currency)}
            </span>
          </div>
        </div>

        {/* Explanation */}
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 mb-4">
          <p className="text-sm text-blue-800">
            Closed accounts are hidden from active views but remain in historical
            reports. You can view closed accounts using the &quot;Show hidden&quot; toggle.
          </p>
        </div>

        {error && (
          <Alert variant="error" className="mb-4" onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}

        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={closeMutation.isPending}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="danger"
            onClick={handleConfirm}
            isLoading={closeMutation.isPending}
            className="flex-1"
          >
            Close Account
          </Button>
        </div>
      </div>
    </div>
  );
}

function WarningIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
      />
    </svg>
  );
}
